import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

// User registration with level selection
export const registerUser = async (email, password, displayName, levelId) => {
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update display name
    await updateProfile(user, { displayName });

    // Create user profile in Firestore
    const userProfile = {
      uid: user.uid,
      email: user.email,
      displayName,
      role: 'student',
      levelId,
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true
    };

    await setDoc(doc(db, 'users', user.uid), userProfile);

    // Create initial subscription (unpaid)
    const subscription = {
      userId: user.uid,
      levelId,
      status: 'unpaid',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(doc(db, 'subscriptions', `${user.uid}_${levelId}`), subscription);

    return { user, profile: userProfile };
  } catch (error) {
    throw error;
  }
};

// User login
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

// User logout
export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

// Get user profile
export const getUserProfile = async (uid) => {
  try {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      throw new Error('User profile not found');
    }
  } catch (error) {
    throw error;
  }
};

// Get user subscription
export const getUserSubscription = async (userId, levelId) => {
  try {
    const docRef = doc(db, 'subscriptions', `${userId}_${levelId}`);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

// Update user profile
export const updateUserProfile = async (uid, updates) => {
  try {
    const docRef = doc(db, 'users', uid);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date()
    });
  } catch (error) {
    throw error;
  }
};

// Auth state listener
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Learning Streak Management
export const getUserStreak = async (uid) => {
  try {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const userData = docSnap.data();
      return userData.learningStreak || {
        currentStreak: 0,
        longestStreak: 0,
        lastActiveDate: null,
        weeklyProgress: 0,
        weeklyGoal: 5,
        streakHistory: []
      };
    }
    
    return {
      currentStreak: 0,
      longestStreak: 0,
      lastActiveDate: null,
      weeklyProgress: 0,
      weeklyGoal: 5,
      streakHistory: []
    };
  } catch (error) {
    console.error('Error getting user streak:', error);
    throw error;
  }
};

export const updateUserStreak = async (uid, streakData) => {
  try {
    const docRef = doc(db, 'users', uid);
    await updateDoc(docRef, {
      learningStreak: streakData,
      updatedAt: serverTimestamp()
    });
    return streakData;
  } catch (error) {
    console.error('Error updating user streak:', error);
    throw error;
  }
};

export const checkAndUpdateLearningStreak = async (uid) => {
  try {
    const currentStreak = await getUserStreak(uid);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day
    
    const lastActive = currentStreak.lastActiveDate ? 
      new Date(currentStreak.lastActiveDate.seconds * 1000) : null;
    
    if (lastActive) {
      lastActive.setHours(0, 0, 0, 0); // Set to start of day
    }
    
    let updatedStreak = { ...currentStreak };
    let daysDiff = 0;
    
    if (!lastActive) {
      // First time user
      updatedStreak.currentStreak = 1;
      updatedStreak.longestStreak = 1;
      updatedStreak.lastActiveDate = today;
      updatedStreak.weeklyProgress = Math.min(updatedStreak.weeklyProgress + 1, updatedStreak.weeklyGoal);
      daysDiff = -1; // First time user
    } else {
      daysDiff = Math.floor((today - lastActive) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 0) {
        // Same day, no change
        return updatedStreak;
      } else if (daysDiff === 1) {
        // Consecutive day
        const newStreak = updatedStreak.currentStreak + 1;
        updatedStreak.currentStreak = newStreak;
        updatedStreak.longestStreak = Math.max(newStreak, updatedStreak.longestStreak);
        updatedStreak.lastActiveDate = today;
        updatedStreak.weeklyProgress = Math.min(updatedStreak.weeklyProgress + 1, updatedStreak.weeklyGoal);
      } else {
        // Streak broken
        updatedStreak.currentStreak = 1;
        updatedStreak.lastActiveDate = today;
        updatedStreak.weeklyProgress = 1;
      }
    }
    
    // Add to streak history
    updatedStreak.streakHistory = [
      ...(updatedStreak.streakHistory || []),
      {
        date: today,
        streak: updatedStreak.currentStreak,
        action: daysDiff <= 1 ? 'maintained' : 'reset'
      }
    ].slice(-30); // Keep last 30 days
    
    await updateUserStreak(uid, updatedStreak);
    return updatedStreak;
  } catch (error) {
    console.error('Error checking and updating learning streak:', error);
    throw error;
  }
};

export default auth;
